import { useEffect, useRef, useState } from 'react'
import Legend from '../Legend/index'
import Search from '../Search/index'
import SidePanel from '../SidePanel/index'
import { useMapData } from '../hooks/useMapData'
import { useStateMachine } from 'little-state-machine'
import { useGoogleMap } from '../hooks/useGoogleMap'
import { useGooglePlacesAutocomplete } from '../hooks/useGooglePlacesAutoComplete'
import { AddMapDataType, MapPropsType } from '../../interfaces'
import {
  removeMarkers,
  resetMap,
  createPolygons,
  setClusterer,
  createOutageAreas,
  addPolygonFeature
} from '../../map_utils'
import { DEFAULT_ERROR_MESSAGE } from 'constant'
import { ErrorOrLoading } from '../../../../residential/shared'
import { updateStore } from '../../state'
import { scrollToRef } from 'utils'

const Map = (props: MapPropsType) => {
  const { state, actions } = useStateMachine({ updateStore })
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = useState<number>(4)
  const map = useGoogleMap(mapContainerRef)
  const searchRef = useRef<HTMLInputElement | null>(null)
  const autocomplete = useGooglePlacesAutocomplete(searchRef)
  const { data, error, loading } = useMapData({
    events: { url: props.getEventsApi, key: props.getEventsKey },
    mapData: { url: props.getOutagesApi, key: props.getOutagesKey }
  })
  const polygons = useRef([])
  const markers = useRef([])
  const markerEvents = useRef([])
  const infoWindow = useRef(null)
  const clusterer = useRef(null)
  const [outageAreas, setOutageAreas] = useState<any>()

  function resetMapData() {
    if (map) {
      actions.updateStore({
        active: true,
        completed: true,
        registered: true,
        major: true
      })
      removeMarkers({
        polygons,
        markers,
        markerEvents,
        clusterer,
        status: 'all'
      })

      // Reset the map
      resetMap(map, infoWindow)

      // Re-create the polygons
      createPolygons({
        features: data,
        polygons,
        markers,
        markerEvents,
        infoWindow,
        map,
        state
      })

      // Add a marker clusterer to manage the markers
      setClusterer({ markers, clusterer, map })

      // Create outage areas
      const tempOutageAreas = createOutageAreas({ markers, state })
      setOutageAreas(tempOutageAreas)
    }
  }
  const addMapData = (mapData: AddMapDataType) => {
    actions.updateStore({
      active: mapData.active,
      completed: mapData.completed,
      registered: mapData.registered,
      major: mapData.major
    })
    // Remove the markers
    removeMarkers({
      polygons,
      markers,
      markerEvents,
      clusterer,
      status: 'all'
    })

    if (map) {
      // Reset the map
      resetMap(map, infoWindow)

      // Filter the data and create polygons
      const filteredData = data.filter(
        (d) =>
          (d?.properties?.status === 'Active' && state?.active) ||
          (d?.properties?.status === 'Completed' && state?.completed) ||
          (d?.properties?.status === 'Closed' && state?.completed) ||
          (d?.properties?.status === 'Registered' && state?.registered) ||
          (d?.properties?.status === 'Major' && state?.major)
      )

      createPolygons({
        polygons,
        features: filteredData,

        infoWindow,
        markers,
        markerEvents,
        map,
        state
      })
      // Add a marker clusterer to manage the markers
      setClusterer({ markers, clusterer, map })
      // Create outage areas
      const tempOutageAreas = createOutageAreas({ markers, state })

      // Copy across any isOpen values from current
      if (outageAreas?.current) {
        ; (outageAreas?.current as Array<any>).forEach((s) => {
          const index = (tempOutageAreas as Array<any>)
            .map((x) => x.suburb)
            .indexOf(s.suburb)
          if (index >= 0) {
            tempOutageAreas[index].isOpen = s.isOpen
          }
        })
      }
      setOutageAreas(tempOutageAreas)
    }
  }

  // Function to update outage area is open flag
  const setIsOpen = (suburb: string) => (isOpen: boolean) => {
    const index = outageAreas
      .map((x: { [index: string]: any }) => x.suburb)
      .indexOf(suburb)
    if (index >= 0) {
      setOutageAreas((obj: Array<{ [index: string]: any }>) => {
        obj[index].isOpen = isOpen
        return obj
      })
    }
  }

  useEffect(() => {
    if (map) {
      map.addListener('zoom_changed', () => {
        const z = map.getZoom() ?? 15
        setZoom(z)
      })
    }
  }, [map])
  // Initialise geo coder and add functionality to polygon
  useEffect(() => {
    if (google.maps.Geocoder) {
      addPolygonFeature()
    }
  }, [])

  useEffect(() => {
    if (data && !loading && !error && map) {
      createPolygons({
        features: data,
        polygons,
        markers,
        markerEvents,
        infoWindow,

        map,
        state
      })
      // Add a marker clusterer to manage the markers
      setClusterer({ markers, clusterer, map })
      // Create outage areas
      const tempOutageAreas = createOutageAreas({ markers, state })
      setOutageAreas(tempOutageAreas)
    }
  }, [data, loading, error, map])

  // Add/remove active map data based on the active flag
  useEffect(() => {
    if (!state?.active) {
      removeMarkers({
        polygons,
        markers,
        markerEvents,
        clusterer,
        status: 'Active'
      })
      const tempOutageAreas = createOutageAreas({
        markers,
        state: { ...state, active: false }
      })
      setOutageAreas(tempOutageAreas)
    }
  }, [state?.active])
  // // Add/remove completed map data based on the completed flag
  useEffect(() => {
    if (!state?.completed) {
      removeMarkers({
        polygons,
        markers,
        markerEvents,
        clusterer,
        status: 'Completed'
      })
      const tempOutageAreas = createOutageAreas({
        markers,
        state: { ...state, completed: false }
      })
      setOutageAreas(tempOutageAreas)
    }
  }, [state?.completed])
  // // Add/remove registered map data based on the registered flag
  useEffect(() => {
    if (!state?.registered) {
      removeMarkers({
        polygons,
        markers,
        markerEvents,
        clusterer,
        status: 'Registered'
      })

      const tempOutageAreas = createOutageAreas({
        markers,
        state: { ...state, registered: false }
      })
      setOutageAreas(tempOutageAreas)
    }
  }, [state?.registered])

  // // Add/remove registered map data based on the registered flag
  useEffect(() => {
    if (!state?.Major) {
      removeMarkers({
        polygons,
        markers,
        markerEvents,
        clusterer,
        status: 'Major'
      })

      const tempOutageAreas = createOutageAreas({
        markers,
        state: { ...state, registered: false }
      })
      setOutageAreas(tempOutageAreas)
    }
  }, [state?.major])

  // bound listener function
  const boundListenerFunction = () => {
    if (map) {
      // Create list of outages
      const bounds = map.getBounds()
      const tempOutages = markers?.current.reduce(
        (array: Array<any>, current: { [index: string]: any }) => {
          if (
            (current.markerData.status === 'Active' && state?.active) ||
            (current.markerData.status === 'Completed' && state?.completed) ||
            (current.markerData.status === 'Closed' && state?.completed) ||
            (current.markerData.status === 'Registered' && state?.registered) ||
            (current.markerData.status === 'Major' && state?.major)
          ) {
            // Check if marker is within bounds
            if (bounds?.contains(current.getPosition())) {
              // Iterate between all affected suburbs and add to result
              if (
                current.markerData.affected_suburbs &&
                current.markerData.affected_suburbs.length > 0
              ) {
                current.markerData.affected_suburbs.forEach((s: string) => {
                  array.push({
                    content: current.markerData,
                    label: s
                  })
                })
              }
            }
          }
          return array
        },
        []
      )

      // Create list of outage areas
      const tempOutageAreas = tempOutages.reduce(
        (array: Array<any>, current: { [index: string]: any }) => {
          const suburb = current.label
          const { status } = current.content
          const index = array.map((x) => x.suburb).indexOf(suburb)
          if (index >= 0) {
            // Increment the counts and add to outages list
            if (status === 'Completed' || status === 'Closed') {
              array[index].completedCount++
            }
            if (status === 'Registered') {
              array[index].upcomingCount++
            }
            if (status === 'Active') {
              array[index].outageCount++
            }
            if (status === 'Major') {
              array[index].majorCount++
            }
            array[index].outages.push(current)
          } else {
            // Add new suburb and sort
            array.push({
              suburb,
              isOpen: false,
              completedCount:
                status === 'Completed' || status === 'Closed' ? 1 : 0,
              upcomingCount: status === 'Registered' ? 1 : 0,
              outageCount: status === 'Active' ? 1 : 0,
              majorCount: status === 'Major' ? 1 : 0,
              outages: [current]
            })
            array.sort((x, y) => {
              const xSuburb = x.suburb
              const ySuburb = y.suburb
              if (xSuburb < ySuburb) {
                return -1
              }
              if (xSuburb > ySuburb) {
                return 1
              }
              return 0
            })
          }
          return array
        },
        []
      )

      // Copy across any isOpen values from current
      if (outageAreas?.current) {
        outageAreas?.current.forEach((s: { [index: string]: any }) => {
          const index = tempOutageAreas
            .map((x: { [index: string]: any }) => x.suburb)
            .indexOf(s.suburb)
          if (index >= 0) {
            tempOutageAreas[index].isOpen = s.isOpen
          }
        })
      }
      setOutageAreas(tempOutageAreas)
    }
  }

  // Set up bound listeners
  useEffect(() => {
    if (map) {
      map.addListener('bounds_changed', boundListenerFunction)
    }
  }, [map])

  useEffect(() => {
    const { active, completed, registered, major } = state
    addMapData({
      active,
      completed,
      registered,
      major
    })
  }, [state?.active, state?.completed, state?.registered, state?.major])

  function scrollToMap() {
    scrollToRef(mapContainerRef)
  }

  return (
    <div id="map-container" className="divide-y divide-x divide-grey-100 rounded-lg overflow-hidden">
      <Search map={map} autocomplete={autocomplete} searchRef={searchRef} />
      <div className="wc-outages__container relative">
        <>
          <a
            href={state?.outagesFormUrl}
            className="absolute top-10 right-10 z-10 hidden md:block btn-cta btn-mobile whitespace-normal"
          >
            Sign up for water supply alerts
          </a>
          {map && (
            <SidePanel
              fresh={
                state?.active &&
                state?.completed &&
                state?.registered &&
                zoom === 4
              }
              content={outageAreas}
              markers={markers}
              resetMapData={resetMapData}
              setIsOpen={setIsOpen}
              map={map}
              scrollToMap={scrollToMap}
            />
          )}
          <ErrorOrLoading
            loading={loading}
            error={(error && new Error(DEFAULT_ERROR_MESSAGE)) ?? undefined}
          />
          <div className="wc-outages__map" ref={mapContainerRef} />
        </>
      </div>
      <Legend />
    </div>
  )
}

export default Map
